<template>
  <div
    v-if="reservationDetailsData"
    class="spa-reservation-details"
  >
    <transition-group name="expand-y">
      <SpaStatusMessage
        v-if="hasCancellationError"
        key="cancellation-error"
        type="error"
        icon="warning-red"
        :message="tc('spaSoft.reservationdetails.cancelPopup.cancelFail', {
          spaEmail: propertyData.email,
          spaPhoneNumber: propertyData.phone,
        })"
      />
      <SpaStatusMessage
        v-for="(message, index) in cancellationMessages"
        :key="`${index}-${message.treatmentName}`"
        type="success"
        icon="checkmark-green"
        :message="tc('spaSoft.reservationdetails.cancelPopup.cancelSuccess', {
          treatmentName: message.treatmentName,
        })"
      />
      <SpaStatusMessage
        v-if="isReservationCancelled"
        key="cancellation-confirmation"
        type="info"
        icon="warning"
        :message="tc('spaSoft.reservationdetails.cancelDetails.confirmationBanner')"
      />
    </transition-group>
    <SpaReservationHeading />
    <SpaReservationInfo />

    <div class="spa-reservation-details__wrapper container">
      <SpaReservationSummary />
      <SpaReservationSidebar />
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions, mapState } from 'vuex'
  import Tracking from '@mixins/tracking'
  import { formatDateZone } from '@utils/date-helpers'
  import SpaReservationHeading from './components/spa-reservation-heading/spa-reservation-heading.vue'
  import SpaReservationInfo from './components/spa-reservation-info/spa-reservation-info.vue'
  import SpaReservationSummary from './components/spa-reservation-summary/spa-reservation-summary.vue'
  import SpaReservationSidebar from './components/spa-reservation-sidebar/spa-reservation-sidebar.vue'
  import SpaStatusMessage from './components/spa-status-message/spa-status-message.vue'

  export default {
    name: 'SpaReservationDetails',
    components: {
      SpaReservationHeading,
      SpaReservationInfo,
      SpaReservationSummary,
      SpaReservationSidebar,
      SpaStatusMessage,
    },
    mixins: [Tracking],
    provide() {
      return {
        mapOptions: this.mapOptions,
        spaReservationPage: this.spaReservationPage,
      }
    },
    props: {
      /**
       * Set of translated labels to be registered in the [i18n store module](/docs/api/store/i18n)
       */
      lang: {
        type: Object,
        default: () => ({})
      },
      /**
       * Array of currencies available on site to be registered in the [currency store module](/docs/api/store/global)
       */
      currencies: {
        type: Array,
        default: () => []
      },
      mapOptions: {
        type: Object,
        required: true,
      },
      /**
       * The page to redirect to the Spa Booking
       */
      spaReservationPage: {
        type: String,
        default: ''
      },
    },
    data() {
      return {
        hasAlreadyTrackedPurchase: false,
      }
    },
    computed: {
      ...mapState('global', { currentLang: 'lang' }),
      ...mapGetters('spaSoft', ['isReservationCancelled', 'hasCancellationError', 'getTimeslotTimeRange', 'getTotalCost', 'getTotalTaxes']),
      ...mapGetters('currency', ['getDisplayPrice', 'orSelected']),
      ...mapGetters('i18n', ['tc']),
      ...mapState('spaSoft', ['propertyData', 'selectedItinerary', 'guestsSelectedData', 'reservationDetailsData', 'cancellationMessages']),
    },
    async created() {
      await this.registerCurrencies(this.currencies)
      await this.registerLang({ scope: 'spaSoft', lang: this.lang })
      this.$track({ event: 'pageView' })
    },
    mounted() {
      this.hasAlreadyTrackedPurchase = sessionStorage.getItem('spaPurchaseTracked') === 'true'
      this.resetSpaBookingData()
      if (!this.hasAlreadyTrackedPurchase) {
        this.trackPurchase()
      }
    },
    methods: {
      ...mapActions('i18n', ['registerLang']),
      ...mapActions('currency', ['registerCurrencies']),
      ...mapActions('spaSoft', ['resetSpaBookingData']),
      trackPurchase() {
        this.hasAlreadyTrackedPurchase = true
        sessionStorage.setItem('spaPurchaseTracked', true)

        this.$track(this.getTrackingData('spaSoft', null, {
          event: 'spaPurchase',
          hotelCode: this.reservationDetailsData.hotelCode,
          stepNumber: 4.1,
          transactionId: this.reservationDetailsData.confirmationCode,
          spaBooking: {
            numberOfGuests: this.reservationDetailsData.guestAvailabilities.length,
            date: this.reservationDetailsData.date,
            timeSlot: this.getTimeslotTimeRange(this.reservationDetailsData.guestAvailabilities),
            totalPrice: this.getTotalCost({
              isReservation: true, formatted: true, noSymbol: true, withIsoCode: false
            }),
            currency: this.orSelected(this.reservationDetailsData.propertyData?.currency?.isoCode),
            taxesAndFees: this.getTotalTaxes({
              isReservation: true, formatted: true, noSymbol: true, withIsoCode: false
            }),
            // payment: {
            //   method: '',
            //   lastFourDigits: ''
            // },
            products: Object.values(this.reservationDetailsData.guestAvailabilities).flatMap((guest, guestIndex) => guest.itinerary.map((treatment) => ({
              guestNumber: guestIndex + 1,
              productType: 'Treatment',
              treatmentType: treatment.treatmentCategory.title,
              treatmentName: treatment.sitecoreTreatmentDetails.title,
              duration: treatment.selectedTimeSlot.duration,
              price: this.getDisplayPrice({
                price: treatment.selectedTimeSlot.apiTreatmentDetails.price.totalCalculated,
                fromCurrency: this.reservationDetailsData.propertyData?.currency?.isoCode,
                toCurrency: this.orSelected(this.reservationDetailsData.propertyData?.currency?.isoCode),
                noSymbol: true
              }),
              currency: this.orSelected(this.reservationDetailsData.propertyData?.currency?.isoCode),
              appointmentTime: `${formatDateZone(treatment.start, null, 'hh:mm a')} - ${formatDateZone(treatment.end, null, 'hh:mm a')}`,
              preferredTherapistGender: treatment.selectedTimeSlot.preferredGender
              // addOns: [
              //   {
              //     addOnName: '',
              //     price: ''
              //   }
              // ]
            })))
          }
        }))
      },
    }
  }
</script>
